import type { BestsellerProduct } from './types';

export type BestsellerProductImageKind = 'product' | 'product_gray' | 'material' | 'celebrity';

export interface BestsellerProductImageInfo {
  src: string;
  kind: BestsellerProductImageKind;
}

interface RankingProductImages {
  productImage: BestsellerProductImageInfo;
  hoverImage: BestsellerProductImageInfo | null;
}

function getImages(product: BestsellerProduct, kind: BestsellerProductImageKind): string[] {
  switch (kind) {
    case 'product':
      return [product.productImage, ...(product.productImages || [])].filter(Boolean);
    case 'product_gray':
      return product.productGrayImages || [];
    case 'material':
      return product.materialImages || [];
    case 'celebrity':
      return product.celebrityImages || (product.modelImage ? [product.modelImage] : []);
    default:
      return [];
  }
}

export function getBestsellerImageCandidates(
  product: BestsellerProduct,
  kind: BestsellerProductImageKind,
): string[] {
  return Array.from(new Set(getImages(product, kind).filter(Boolean)));
}

function getFirstDistinctImage(
  product: BestsellerProduct,
  kind: BestsellerProductImageKind,
  currentSrc: string,
  startIndex = 0,
): BestsellerProductImageInfo | null {
  const src = getImages(product, kind).find(
    (candidate, index) => index >= startIndex && candidate !== currentSrc,
  );

  return src ? { src, kind } : null;
}

export function shouldCoverBestsellerImage(kind: BestsellerProductImageKind): boolean {
  return kind === 'material' || kind === 'celebrity';
}

export function getTopRankingProductImages(product: BestsellerProduct): RankingProductImages {
  const productImage = { src: product.productImage, kind: 'product' as const };
  const hoverImage =
    getFirstDistinctImage(product, 'product', productImage.src, 1) ??
    getFirstDistinctImage(product, 'product_gray', productImage.src) ??
    getFirstDistinctImage(product, 'material', productImage.src) ??
    getFirstDistinctImage(product, 'celebrity', productImage.src);

  return {
    productImage,
    hoverImage,
  };
}

export function getCompactRankingProductImages(product: BestsellerProduct): RankingProductImages {
  const productImage = { src: product.productImage, kind: 'product' as const };
  const hoverImage =
    getFirstDistinctImage(product, 'material', productImage.src) ??
    getFirstDistinctImage(product, 'celebrity', productImage.src) ??
    getFirstDistinctImage(product, 'product', productImage.src, 1) ??
    getFirstDistinctImage(product, 'product_gray', productImage.src);

  return {
    productImage,
    hoverImage,
  };
}
